import React from 'react';
import { useTranslation } from 'react-i18next';

interface PremiumUpgradeSuccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: {
    analysis?: {
      strengthScore?: number;
    };
    previousStrengthScore?: number;
    strengthScore?: number;
    documentsGenerated?: number;
    isPremiumAnalysis?: boolean;
  };
}

const PremiumUpgradeSuccessModal: React.FC<PremiumUpgradeSuccessModalProps> = ({
  isOpen,
  onClose,
  data
}) => {
  const { t } = useTranslation();

  if (!isOpen) return null;

  const previousScore = data.previousStrengthScore || 0;
  const newScore = data.analysis?.strengthScore || data.strengthScore || 0;
  const improvement = newScore - previousScore;
  const documentsGenerated = data.documentsGenerated || 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full p-8 shadow-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center mb-6">
          <div className="flex-shrink-0">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          </div>
          <div className="ml-4">
            <h2 className="text-2xl font-bold text-gray-900">
              {t('lawsuit.premiumUpgradeSuccess.title', 'Premium Upgrade Complete!')}
            </h2>
            <p className="text-gray-600 mt-1">
              {t('lawsuit.premiumUpgradeSuccess.subtitle', 'Your case has been enhanced with premium AI analysis')}
            </p>
          </div>
        </div>

        {/* Case Strength Comparison */}
        <div className="bg-gradient-to-r from-blue-50 to-green-50 rounded-lg p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {t('lawsuit.premiumUpgradeSuccess.caseStrengthTitle', 'Case Strength Improvement')}
          </h3>
          
          <div className="flex items-center justify-between mb-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-700">{previousScore}/100</div>
              <div className="text-sm text-gray-600">
                {t('lawsuit.premiumUpgradeSuccess.before', 'Before')}
              </div>
            </div>
            
            <div className="flex-1 mx-6">
              <div className="flex items-center">
                <div className="flex-1 h-2 bg-gray-200 rounded-full">
                  <div 
                    className="h-2 bg-blue-500 rounded-full transition-all duration-1000"
                    style={{ width: `${previousScore}%` }}
                  ></div>
                </div>
                <svg className="w-8 h-8 text-green-600 mx-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
                <div className="flex-1 h-2 bg-gray-200 rounded-full">
                  <div 
                    className="h-2 bg-green-500 rounded-full transition-all duration-1000"
                    style={{ width: `${newScore}%` }}
                  ></div>
                </div>
              </div>
            </div>
            
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{newScore}/100</div>
              <div className="text-sm text-gray-600">
                {t('lawsuit.premiumUpgradeSuccess.after', 'After')}
              </div>
            </div>
          </div>

          {improvement > 0 && (
            <div className="text-center">
              <div className="inline-flex items-center px-3 py-1 rounded-full bg-green-100 text-green-800">
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 11l5-5m0 0l5 5m-5-5v12" />
                </svg>
                <span className="text-sm font-medium">
                  +{improvement} {t('lawsuit.premiumUpgradeSuccess.pointsImprovement', 'points improvement')}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Gains Section */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {t('lawsuit.premiumUpgradeSuccess.gainsTitle', 'What You Gained')}
          </h3>
          
          <div className="space-y-3">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <div>
                <div className="font-medium text-gray-900">
                  {t('lawsuit.premiumUpgradeSuccess.enhancedAnalysis', 'Enhanced AI Analysis')}
                </div>
                <div className="text-sm text-gray-600">
                  {t('lawsuit.premiumUpgradeSuccess.enhancedAnalysisDesc', 'Advanced AI models provided deeper insights into your case')}
                </div>
              </div>
            </div>

            {documentsGenerated > 0 && (
              <div className="flex items-start">
                <svg className="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <div>
                  <div className="font-medium text-gray-900">
                    {t('lawsuit.premiumUpgradeSuccess.premiumDocuments', 'Premium Legal Documents')}
                  </div>
                  <div className="text-sm text-gray-600">
                    {t('lawsuit.premiumUpgradeSuccess.premiumDocumentsDesc', `${documentsGenerated} enhanced document(s) generated with advanced legal formatting`)}
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-start">
              <svg className="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <div>
                <div className="font-medium text-gray-900">
                  {t('lawsuit.premiumUpgradeSuccess.improvedStrength', 'Improved Case Strength Assessment')}
                </div>
                <div className="text-sm text-gray-600">
                  {t('lawsuit.premiumUpgradeSuccess.improvedStrengthDesc', 'More accurate evaluation of your legal position and success probability')}
                </div>
              </div>
            </div>

            <div className="flex items-start">
              <svg className="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <div>
                <div className="font-medium text-gray-900">
                  {t('lawsuit.premiumUpgradeSuccess.detailedRecommendations', 'Detailed Legal Recommendations')}
                </div>
                <div className="text-sm text-gray-600">
                  {t('lawsuit.premiumUpgradeSuccess.detailedRecommendationsDesc', 'Comprehensive guidance on next steps and strategy improvements')}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Strength Comparison Message */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <div>
              <div className="font-medium text-blue-900">
                {t('lawsuit.premiumUpgradeSuccess.strengthComparisonTitle', 'Your Case is Now Stronger')}
              </div>
              <div className="text-sm text-blue-800 mt-1">
                {improvement > 0 ? (
                  t('lawsuit.premiumUpgradeSuccess.strengthComparisonDesc', {
                    improvement,
                    defaultValue: `Your case strength improved by ${improvement} points, indicating a significantly stronger legal position compared to before the premium upgrade.`
                  })
                ) : (
                  t('lawsuit.premiumUpgradeSuccess.strengthMaintained',
                    'Your case maintains its strong position with enhanced analysis and premium documents.'
                  )
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Action Button */}
        <div className="flex justify-end">
          <button
            className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors duration-200 shadow-md hover:shadow-lg"
            onClick={onClose}
          >
            {t('lawsuit.premiumUpgradeSuccess.viewUpdatedCase', 'View Updated Case')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PremiumUpgradeSuccessModal;